import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  Icon,
  Link,
  Sidebar,
  Spinner,
  Tag,
  Text,
  Title,
} from '@nimbus-ds/components';
import { ChevronLeftIcon } from '@nimbus-ds/icons';
import { useParams } from 'react-router';
import { OrderBulkDto, OrderDetailsResponseDto } from '@tiendanube/common';
import { Country } from '@tiendanube/common/src/enums';
import { useNavegate } from 'App/hooks';
import { useGetCountry } from 'domains/Auth/hooks';
import { useFormatCurrency } from 'domains/Billing/hooks';
import { Divider } from 'domains/NuvemChat/pages/components/Divider';
import { NE_PRINT_LABEL_SOURCE } from 'domains/NuvemEnvioCommons/constants';
import useNuvemEnvioTranslations from 'domains/NuvemEnvioCommons/hooks/useNuvemEnvioTranslations/useNuvemEnvioTranslations';
import {
  trackingNuvemEnvioCreditRechargeLabelsModalCancelButton,
  trackingNuvemEnvioCreditRechargeLabelsModalContinueButton,
} from 'domains/NuvemEnvioCommons/tracking';
import { useStoreInfo } from 'domains/PartnersApps/hooks';
import { useGetNuvemEnvioConfigs } from 'domains/Shipping/DeliveryMethods/hooks';
import useCreditRechargeNuvemEnvioLabels from '../../hooks/useCreditRechargeNuvemEnvioLabels/useCreditRechargeNuvemEnvioLabels';
import useNuvemEnvioPrintLabel from '../../hooks/useNuvemEnvioPrintLabel/useNuvemEnvioPrintLabel';
import useSimulateNuvemEnvioLabels from '../../hooks/useSimulateNuvemEnvioLabels/useSimulateNuvemEnvioLabels';
import {
  extractFulfillmentOrdersIds,
  buildRechargeCallbackPath,
  transformBulkOrdersToOrdersForPrintLabel,
} from '../../utils';

type OrdersPrintLabelModalProps =
  | OrdersPrintLabelModalFromOrdersListProps
  | OrdersPrintLabelModalFromOrderDetailsProps;

type OrdersPrintLabelModalFromOrdersListProps = {
  onClose: () => void;
  source: NE_PRINT_LABEL_SOURCE.ORDERS_LIST;
  selectedRowsId: string[];
  bulkOrders: OrderBulkDto[];
};

type OrdersPrintLabelModalFromOrderDetailsProps = {
  onClose: () => void;
  source: NE_PRINT_LABEL_SOURCE.ORDER_DETAILS;
  orderDetails: OrderDetailsResponseDto;
};

function OrdersPrintLabelModal(
  props: Readonly<OrdersPrintLabelModalProps>,
): JSX.Element {
  const t = useNuvemEnvioTranslations();
  const { id: storeId } = useStoreInfo();
  const { goTo } = useNavegate();
  const { id: orderId } = useParams<{ id: string }>();
  const { data: simulateNuvemEnvioLabelsData } = useSimulateNuvemEnvioLabels();
  const { createNuvemEnvioRecharge, status } =
    useCreditRechargeNuvemEnvioLabels();
  const { closeNuvemEnvioPrintLabelModal } = useNuvemEnvioPrintLabel();
  const { shouldShowAsyncProcessing, shouldUseBulkAsyncProcess } =
    useGetNuvemEnvioConfigs();

  const country = useGetCountry();
  const formatCurrency = useFormatCurrency();

  const [error, setError] = useState<boolean>(false);

  const totalImportedOrders =
    props.source === NE_PRINT_LABEL_SOURCE.ORDERS_LIST
      ? props?.selectedRowsId.length -
        (simulateNuvemEnvioLabelsData?.errors?.length ?? 0)
      : props?.orderDetails?.fulfillmentOrders?.length ?? 0;

  const handleCreditRecharge = async () => {
    trackingNuvemEnvioCreditRechargeLabelsModalContinueButton({
      storeId,
      orderQuantity: totalImportedOrders,
      totalAmount:
        simulateNuvemEnvioLabelsData?.balanceInfo?.suggestedRechargeAmount ?? 0,
      finalAmount:
        simulateNuvemEnvioLabelsData?.balanceInfo?.finalBalanceAmount ?? 0,
      hasCreditApplied:
        simulateNuvemEnvioLabelsData?.balanceInfo?.shouldRecharge ?? false,
    });
    try {
      const fulfillmentOrderIds =
        props.source === NE_PRINT_LABEL_SOURCE.ORDERS_LIST
          ? extractFulfillmentOrdersIds(
              transformBulkOrdersToOrdersForPrintLabel(
                props?.selectedRowsId,
                props?.bulkOrders ?? [],
              ),
            )
          : props?.orderDetails?.fulfillmentOrders?.map(
              (fulfillment) => fulfillment.id,
            ) ?? [];

      const response = await createNuvemEnvioRecharge({
        amount:
          simulateNuvemEnvioLabelsData?.balanceInfo?.suggestedRechargeAmount ??
          0,
        callbackUrl: buildRechargeCallbackPath({
          source: props.source,
          fulfillmentOrderIds,
          orderId,
          asyncProcess: shouldShowAsyncProcessing || shouldUseBulkAsyncProcess,
        }),
      });
      closeNuvemEnvioPrintLabelModal();

      goTo(response.checkoutUrl);
    } catch (error) {
      setError(true);
    }
  };

  const handleCancel = () => {
    trackingNuvemEnvioCreditRechargeLabelsModalCancelButton(
      storeId,
      totalImportedOrders,
      simulateNuvemEnvioLabelsData?.balanceInfo?.suggestedRechargeAmount ?? 0,
    );
    props.onClose();
  };

  useEffect(
    () => () => {
      closeNuvemEnvioPrintLabelModal();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return (
    <Sidebar
      padding="base"
      position="right"
      open
      zIndex="800"
      maxWidth={{ xs: '100%', md: '375px' }}
    >
      <Sidebar.Header>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="flex-start"
          gap="6"
          justifyContent="flex-start"
        >
          <Link as="a" onClick={props.onClose} textDecoration="none">
            <Icon color="primary-textHigh" source={<ChevronLeftIcon />} />
          </Link>

          <Title>{t('creditRechargeNuvemEnvioLabels.title')}</Title>
        </Box>
      </Sidebar.Header>
      <Sidebar.Body>
        <Box display="flex" flexDirection="column" gap="4">
          <Text>{t('creditRechargeNuvemEnvioLabels.text')}</Text>

          <Box
            display="flex"
            flexDirection="row"
            gap="2"
            alignItems="center"
            justifyContent="space-between"
          >
            <Text color="neutral-textDisabled" fontSize="caption">
              {t('creditRechargeNuvemEnvioLabels.importedOrders')}
            </Text>
            <Tag appearance="neutral">
              {totalImportedOrders}{' '}
              {t('creditRechargeNuvemEnvioLabels.totalOrders')}{' '}
              {props.source === NE_PRINT_LABEL_SOURCE.ORDERS_LIST
                ? props?.selectedRowsId.length
                : props?.orderDetails?.fulfillmentOrders?.length ?? 0}
            </Tag>
          </Box>

          <Card>
            <Card.Body>
              <Box display="flex" flexDirection="column" gap="2">
                {country !== Country.MX && (
                  <Text fontSize="caption">
                    {t('creditRechargeNuvemEnvioLabels.review.title')}
                  </Text>
                )}
                {country === Country.MX &&
                simulateNuvemEnvioLabelsData?.balanceInfo
                  ?.currentBalanceAmount === 0 ? (
                  <Box
                    display="flex"
                    flexDirection="row"
                    gap="2"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Text>
                      {t(
                        'creditRechargeNuvemEnvioLabels.review.totalLabelsWithBalanceIsZero',
                      )}
                    </Text>
                    <Text color="neutral-textDisabled" fontWeight="bold">
                      {formatCurrency(
                        simulateNuvemEnvioLabelsData?.balanceInfo
                          ?.chargeableAmount ?? 0,
                      )}
                    </Text>
                  </Box>
                ) : (
                  <>
                    <Box
                      display="flex"
                      flexDirection="row"
                      gap="2"
                      alignItems="center"
                      justifyContent="space-between"
                      order={country === Country.MX ? '2' : '1'}
                    >
                      <Text
                        color={
                          simulateNuvemEnvioLabelsData?.balanceInfo
                            ?.currentBalanceAmount &&
                          simulateNuvemEnvioLabelsData?.balanceInfo
                            ?.currentBalanceAmount > 0
                            ? 'success-interactive'
                            : 'neutral-textDisabled'
                        }
                      >
                        {t(
                          'creditRechargeNuvemEnvioLabels.review.amountAvailable',
                        )}
                      </Text>
                      <Text color="success-interactive">
                        +{' '}
                        {formatCurrency(
                          simulateNuvemEnvioLabelsData?.balanceInfo
                            ?.currentBalanceAmount ?? 0,
                        )}
                      </Text>
                    </Box>

                    <Box
                      display="flex"
                      flexDirection="row"
                      gap="2"
                      alignItems="center"
                      justifyContent="space-between"
                      order={country === Country.MX ? '1' : '2'}
                    >
                      <Text>
                        {t('creditRechargeNuvemEnvioLabels.review.totalLabels')}
                      </Text>
                      <Text>
                        {formatCurrency(
                          simulateNuvemEnvioLabelsData?.balanceInfo
                            ?.chargeableAmount ?? 0,
                        )}
                      </Text>
                    </Box>

                    <Box order="3">
                      <Divider />
                    </Box>

                    <Box
                      display="flex"
                      flexDirection="row"
                      gap="2"
                      alignItems="center"
                      justifyContent="space-between"
                      order="4"
                    >
                      <Text fontWeight="bold">
                        {t('creditRechargeNuvemEnvioLabels.review.totalAmount')}
                      </Text>
                      <Text fontWeight="bold">
                        {formatCurrency(
                          simulateNuvemEnvioLabelsData?.balanceInfo
                            ?.suggestedRechargeAmount ?? 0,
                        )}
                      </Text>
                    </Box>
                  </>
                )}
              </Box>
            </Card.Body>
          </Card>

          {error && (
            <Box display="flex" flexDirection="column" gap="2">
              <Text color="danger-interactive">
                {t('creditRechargeNuvemEnvioLabels.error.title')}
              </Text>
            </Box>
          )}

          <Box
            display="flex"
            flexDirection="row"
            gap="2"
            justifyContent="flex-end"
          >
            <Button appearance="neutral" onClick={handleCancel}>
              {t('creditRechargeNuvemEnvioLabels.buttons.cancel')}
            </Button>
            <Button
              appearance="primary"
              onClick={handleCreditRecharge}
              disabled={status.isLoading}
            >
              {status.isLoading && (
                <Spinner size="small" color="currentColor" />
              )}
              {t('creditRechargeNuvemEnvioLabels.buttons.continue')}
            </Button>
          </Box>
        </Box>
      </Sidebar.Body>
    </Sidebar>
  );
}

export default OrdersPrintLabelModal;
